<template>
	<div class="d-flex flex-column" :class="separatedContainers ? 'gap-2' : ''">
		<div
			class="d-flex justify-between cursor-pointer align-center collapsable-header"
			:class="headerClasses"
			:style="{ maxHeight: headerMaxHeight }"
			@click="toggle"
		>
			<slot name="title" />
			<Icon
				icon="chevron"
				class="arrow"
				:class="iconClasses"
				:rotate="isOpen ? 'down' : ''"
				style="margin-inline-start: auto"
			/>
		</div>
		<Transition
			@enter="onEnter"
			@after-enter="onAfterEnter"
			@leave="onLeave"
			@after-leave="onAfterLeave"
		>
			<div v-if="isOpen" :class="contentClasses">
				<slot name="content" />
			</div>
		</Transition>
	</div>
</template>

<script setup lang="ts">
	import { computed, ref } from 'vue'
	import Icon from '~/components/passporterUi/Icon.vue'

	interface Props {
		shouldOpen?: boolean
		modelValue?: boolean | null
		separatedContainers?: boolean
		headerClasses?: string
		contentClasses?: string
		headerMaxHeight?: string
		iconClasses?: string
	}
	const props = withDefaults(defineProps<Props>(), {
		modelValue: null,
		headerClasses: '',
		contentClasses: '',
		headerMaxHeight: '',
		iconClasses: '',
	})
	const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()

	const internalOpen = ref(props.shouldOpen)
	const isOpen = computed(() => (props.modelValue !== null ? props.modelValue : internalOpen.value))

	const toggle = () => {
		if (props.modelValue !== null) {
			emit('update:modelValue', !props.modelValue)
		} else {
			internalOpen.value = !internalOpen.value
		}
	}

	const onEnter = (el: Element) => {
		const element = el as HTMLElement
		element.style.maxHeight = '0'
		element.style.overflow = 'hidden'
		requestAnimationFrame(() => {
			element.style.transition = 'max-height 0.3s ease'
			element.style.maxHeight = `${element.scrollHeight}px`
		})
	}

	const onAfterEnter = (el: Element) => {
		const element = el as HTMLElement
		element.style.maxHeight = ''
		element.style.overflow = ''
		element.style.transition = ''
	}

	const onLeave = (el: Element) => {
		const element = el as HTMLElement
		element.style.maxHeight = `${element.scrollHeight}px`
		element.style.overflow = 'hidden'
		requestAnimationFrame(() => {
			element.style.transition = 'max-height 0.3s ease'
			element.style.maxHeight = '0'
		})
	}

	const onAfterLeave = (el: Element) => {
		const element = el as HTMLElement
		element.style.maxHeight = ''
		element.style.overflow = ''
		element.style.transition = ''
	}
</script>

<style lang="scss" scoped>
	.arrow {
		transition: transform 0.3s;
	}
</style>
